import { PhoneProvider } from '@hit/react-jssip';
import { Outlet } from '@tanstack/react-router';
import { useAtomValue } from 'jotai';

import { useAsteriskUrl } from '@/api/use-asterisk-url';
import { useBranchFeatures } from '@/api/use-branch-features';
import ringtone from '@/assets/ringtone.wav';
import Loading from '@/components/loading';
import ActiveCallAudio from '@/features/active-call-audio';
import Background20HzAudio from '@/features/background-20hz-audio';
import ExtensionStatus, {
  ExtensionStatusBar,
} from '@/features/extension-status';
import Navbar from '@/features/navbar';
import NotificationPermissionBanner from '@/features/notification-permission-banner';
import Status from '@/features/status';
import { useLogin } from '@/features/webrtc-app/hooks/useLogin';
import { useNavigateOnIncomingCall } from '@/features/webrtc-app/hooks/useNavigateOnIncomingCall';
import { useNotifyOnIncomingCall } from '@/features/webrtc-app/hooks/useNotifyOnIncomingCall';
import { useOnAnswerFailure } from '@/features/webrtc-app/hooks/useOnAnswerFailure';
import { useOnCallActionListener } from '@/features/webrtc-app/hooks/useOnCallActionListener';
import { useOnCallEnded } from '@/features/webrtc-app/hooks/useOnCallEnded';
import { useOnExtensionReadyNotifier } from '@/features/webrtc-app/hooks/useOnExtensionReadyNotifier';
import { useOnMissedCall } from '@/features/webrtc-app/hooks/useOnMissedCall';
import { useScreenPopTriggers } from '@/features/webrtc-app/hooks/useScreenPopTriggers';
import { useExtension } from '@/hooks/use-extension';
import { registerSipLogSecret } from '@/lib/sip-log-privacy';
import { ringTimeoutAtom } from '@/store/atoms';

export default function WebrtcApp() {
  const asteriskUrlQuery = useAsteriskUrl();
  const featuresQuery = useBranchFeatures();
  const hasQueueMetrics = featuresQuery.isError
    ? undefined
    : featuresQuery.data?.queueMetrics;
  const extension = useExtension();
  registerSipLogSecret(extension?.password);
  const ringTimeout = useAtomValue(ringTimeoutAtom);
  const { isLoggedOnQueue } = useLogin();
  const onCallEnded = useOnCallEnded();
  const onMissedCall = useOnMissedCall();
  const onAnswerFailure = useOnAnswerFailure();
  useScreenPopTriggers();
  useNavigateOnIncomingCall();
  useNotifyOnIncomingCall();
  useOnExtensionReadyNotifier();
  useOnCallActionListener();

  if (hasQueueMetrics === undefined || !asteriskUrlQuery.data || !extension)
    return (
      <>
        {hasQueueMetrics === false && (
          <ExtensionStatusBar number={extension?.number} />
        )}
        <Loading />
      </>
    );

  const isReady = hasQueueMetrics === false || isLoggedOnQueue;

  return (
    <PhoneProvider
      // Traces go through sip-logging; console output requires explicit opt-in.
      debug
      singleCallMode
      asteriskWsUrl={asteriskUrlQuery.data}
      extension={{
        number: extension.number,
        password: extension?.password ?? '',
        ringTimeout: ringTimeout >= 1 ? ringTimeout : undefined,
      }}
      onCallEnded={onCallEnded}
      ringtone={ringtone}
      onMissedCall={onMissedCall}
      onAnswerFailure={onAnswerFailure}
    >
      {isReady ? (
        <>
          {hasQueueMetrics === true ? (
            <Status />
          ) : (
            <ExtensionStatus number={extension.number} />
          )}
          <NotificationPermissionBanner />
          <ActiveCallAudio />
          <Background20HzAudio />
          <div className="flex grow overflow-hidden bg-[#fafafa]">
            <Outlet />
          </div>
          <Navbar />
        </>
      ) : (
        <Loading />
      )}
    </PhoneProvider>
  );
}
